import lodashSnakeCase from 'lodash/snakeCase';
import lodashCamelCase from 'lodash/camelCase';
import lodashInvert from 'lodash/invert';
import lodashCloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import {getSortName, ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';
import {TYPE_QUALITY_ISSUES} from '../constants/issue-types';
import {camelizeProperties, decamelizeProperties} from './jsonapi-helpers';
import {doesViewableHaveSheetInfo, getLinkedDocument, hasLinkedDocument} from './issues-helpers';

/* eslint-disable no-return-assign */
export const V2_TO_V1_ATTRIBUTES = {
  [ISSUE_ATTRIBUTE_NAMES.ID]: 'identifier',
  [ISSUE_ATTRIBUTE_NAMES.TITLE]: 'title',
  [ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]: 'description',
  [ISSUE_ATTRIBUTE_NAMES.STATUS]: 'status',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: 'assigned_to',
  [ISSUE_ATTRIBUTE_NAMES.CONTAINER_ID]: 'container_id',
  [ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID]: 'root_cause_id',
  [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: 'ng_issue_type_id',
  [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: 'ng_issue_subtype_id',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: 'assigned_to',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: 'assigned_to_type',
  [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: 'due_date',
  [ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]: 'lbs_location',
  [ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]: 'location_description',
  [ISSUE_ATTRIBUTE_NAMES.SNAPSHOT_URN]: 'snapshot_urn',
  [ISSUE_ATTRIBUTE_NAMES.OWNER_ID]: 'owner',
  [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ATTRIBUTES]: 'permitted_attributes',
  [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ACTIONS]: 'permitted_actions',
  [ISSUE_ATTRIBUTE_NAMES.PERMITTED_STATUSES]: 'permitted_statuses',
  [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: 'custom_attributes',
  [ISSUE_ATTRIBUTE_NAMES.ISSUE_TEMPLATE_ID]: 'issue_template_id',
  [ISSUE_ATTRIBUTE_NAMES.COMMENT_COUNT]: 'comment_count',
  [ISSUE_ATTRIBUTE_NAMES.ATTACHMENT_COUNT]: 'attachment_count',
  [ISSUE_ATTRIBUTE_NAMES.OPENED_BY]: 'opened_by',
  [ISSUE_ATTRIBUTE_NAMES.OPENED_AT]: 'opened_at',
  [ISSUE_ATTRIBUTE_NAMES.CLOSED_BY]: 'closed_by',
  [ISSUE_ATTRIBUTE_NAMES.CLOSED_AT]: 'closed_at',
  [ISSUE_ATTRIBUTE_NAMES.UPDATED_BY]: 'updated_by',
  [ISSUE_ATTRIBUTE_NAMES.UPDATED_AT]: 'updated_at',
  [ISSUE_ATTRIBUTE_NAMES.CREATED_BY]: 'created_by',
  [ISSUE_ATTRIBUTE_NAMES.CREATED_AT]: 'created_at',
  [ISSUE_ATTRIBUTE_NAMES.LINKS]: 'quality_urns',
  [ISSUE_ATTRIBUTE_NAMES.OFFICIAL_RESPONSE]: 'answer',
  // Sort fields
  [getSortName(ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID)]: 'ng_issue_type',
  [getSortName(ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID)]: 'ng_issue_subtype',
  [getSortName(ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID)]: 'root_cause'
};

const V2_TO_V1_OVERRIDE_PERMITTED_ATTRIBUTES = {
  [ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS]: 'pushpin_attributes'
};

const V2_TO_V1_PERMITTED_ATTRIBUTES = {
  ...V2_TO_V1_ATTRIBUTES,
  ...V2_TO_V1_OVERRIDE_PERMITTED_ATTRIBUTES
};

export const V1_TO_V2_ATTRIBUTES = lodashInvert(V2_TO_V1_ATTRIBUTES);

const V1_TO_V2_OVERRIDE_PERMITTED_ATTRIBUTES = lodashInvert(V2_TO_V1_OVERRIDE_PERMITTED_ATTRIBUTES);
const V1_TO_V2_PERMITTED_ATTRIBUTES = {
  ...V1_TO_V2_ATTRIBUTES,
  ...V1_TO_V2_OVERRIDE_PERMITTED_ATTRIBUTES
};

const makeConverter = (attributeMap, fallback) => value =>
  attributeMap.hasOwnProperty(value) ? attributeMap[value] : fallback(value);

export const toV1 = makeConverter(V2_TO_V1_ATTRIBUTES, lodashSnakeCase);
export const toV2 = makeConverter(V1_TO_V2_ATTRIBUTES, lodashCamelCase);
const toV1PermittedAttributes = makeConverter(V2_TO_V1_PERMITTED_ATTRIBUTES, lodashSnakeCase);
const toV2PermittedAttributes = makeConverter(V1_TO_V2_PERMITTED_ATTRIBUTES, lodashCamelCase);

const deleteUndefinedKeys = obj => {
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined) {
      delete obj[key];
      return;
    }
    if (typeof value === 'object' && value !== null) {
      deleteUndefinedKeys(value);
    }
  });
};

export const toV2Structure = (resource, extraData) => {
  resource = lodashCloneDeep(resource);
  const {id, type, attributes} = resource;

  if (type === TYPE_QUALITY_ISSUES) {
    const result = {
      id,
      type,
      permittedAttributes: attributes.permitted_attributes
        .filter(attr => V1_TO_V2_PERMITTED_ATTRIBUTES[attr])
        .map(toV2PermittedAttributes),
      links: []
    };

    if (attributes.target_urn) {
      result.linkedDocuments = [
        {
          urn: attributes.target_urn,
          closedAtVersion: attributes.close_version,
          createdAtVersion: attributes.starting_version,
          ...(attributes.pushpin_attributes
            ? {
              type: attributes.pushpin_attributes.type,
              createdBy: attributes.pushpin_attributes.created_by,
              createdAt: attributes.pushpin_attributes.created_at,
              closedBy: attributes.pushpin_attributes.closed_by,
              closedAt: attributes.pushpin_attributes.closed_at
            }
            : {}),
          details: {
            viewable: {
              id: attributes.sheet_metadata?.sheetGuid ?? null,
              name: attributes.sheet_metadata?.sheetName ?? null,
              is3D: attributes.sheet_metadata?.is3D ?? null
            },
            ...(attributes.pushpin_attributes
              ? {
                position: attributes.pushpin_attributes.location,
                objectId: attributes.pushpin_attributes.object_id,
                externalId: attributes.pushpin_attributes.external_id,
                viewerState: attributes.pushpin_attributes.viewer_state
              }
              : {})
          }
        }
      ];
    }

    if (attributes.answer || attributes.answered_at || attributes.answered_by) {
      result.officialResponse = {
        response: attributes.answer,
        respondedAt: attributes.answered_at,
        respondedBy: attributes.answered_by
      };
    }

    if (attributes.quality_urns) {
      const [urn1, urn2, urn3] = attributes.quality_urns;
      result.links[0] = {
        type: 'checklist',
        data: {
          urn1,
          urn2,
          urn3
        }
      };
    }

    if (attributes.custom_attributes) {
      result.customAttributes = attributes.custom_attributes.map(attr => ({
        attributeDefinitionId: attr.id,
        value: attr.value,
        type: attr.type,
        title: attr.title
      }));
    }

    Object.values(ISSUE_ATTRIBUTE_NAMES)
      .filter(attribute => !result.hasOwnProperty(attribute))
      .forEach(attribute => (result[attribute] = attributes[toV1(attribute)]));

    if (extraData) {
      const {meta, links} = extraData;
      const pagination = {};
      if (meta) {
        Object.assign(pagination, {
          limit: meta.page.limit,
          offset: meta.page.offset,
          totalResults: meta.record_count
        });
      }
      if (links) {
        const {next, prev: previous} = links;
        Object.assign(pagination, {
          next,
          previous
        });
      }

      deleteUndefinedKeys(pagination);
      if (!isEmpty(pagination)) {
        Object.assign(result, {pagination});
      }
    }

    deleteUndefinedKeys(result);
    return result;
  }

  return camelizeProperties({
    id,
    type,
    ...attributes,
    ...extraData
  });
};

export const toV1Structure = (resource, {extraData = {}, isIssue = false} = {}) => {
  resource = lodashCloneDeep(resource);
  if (isIssue || resource[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]) {
    const result = {};
    if (resource.id) {
      result.id = resource.id;
    }
    if (resource.permittedAttributes) {
      result.permitted_attributes = resource.permittedAttributes.map(toV1PermittedAttributes);
    }

    if (hasLinkedDocument(resource)) {
      const document = getLinkedDocument(resource);
      const details = document.details || {};
      const {viewable} = details;

      Object.assign(result, {
        target_urn: document.urn,
        starting_version: document.createdAtVersion,
        close_version: document.closedAtVersion,
        ...(document.urn || Object.keys(details).length > 0
          ? {
            pushpin_attributes: {
              type: document.type,
              created_by: document.createdBy,
              created_at: document.createdAt,
              closed_by: document.closedBy,
              closed_at: document.closedAt,
              location: details.position,
              object_id: details.objectId,
              viewer_state: details.viewerState,
              external_id: details.externalId,
              attributes_version: details.viewerState ? details.viewerState.attributesVersion : undefined
            },
            sheet_metadata: doesViewableHaveSheetInfo(viewable)
              ? {
                is3D: viewable.is3D,
                sheetGuid: viewable.guid || viewable.id, // BE sends both fields with same value when FF is ON, When FF is OFF, ID will be present and GUID will be undefined
                sheetName: viewable.name
              }
              : null
          }
          : {})
      });
    }

    if (resource.links?.length > 0) {
      const [link] = resource.links;
      if (link.type === 'checklist') {
        const {urn1, urn2, urn3} = link.data;
        result.quality_urns = [urn1, urn2, urn3];
      }
    }

    if (resource.customAttributes) {
      result.custom_attributes =
        resource.customAttributes.length > 0
          ? resource.customAttributes.map(attr => ({
            id: attr.attributeDefinitionId,
            value: attr.value,
            type: attr.type,
            title: attr.title
          }))
          : undefined;
    }

    if (resource.officialResponse) {
      Object.assign(result, {
        answer: resource.officialResponse.response,
        answered_at: resource.officialResponse.respondedAt,
        answered_by: resource.officialResponse.respondedBy
      });
    }

    if (resource.pagination) {
      const {limit, offset, totalResults, next, previous: prev} = resource.pagination;
      Object.assign(result, {
        meta: {
          page: {
            offset,
            limit
          },
          record_count: totalResults
        },
        links: {
          next,
          prev
        }
      });
    }

    if (resource[ISSUE_ATTRIBUTE_NAMES.COMMENT]) {
      result.comments_attributes = [resource[ISSUE_ATTRIBUTE_NAMES.COMMENT]];
    }

    Object.values(V2_TO_V1_ATTRIBUTES)
      .filter(attribute => resource[toV2(attribute)] !== undefined)
      .filter(attribute => !result.hasOwnProperty(attribute))
      .forEach(attribute => (result[attribute] = resource[toV2(attribute)]));

    deleteUndefinedKeys(result);
    return result;
  }

  return decamelizeProperties(resource, extraData);
};
